import Cookie from 'js-cookie';
import qs from 'qs';
import stringhash from 'string-hash';

import Sentry from 'shared/helpers/sentry';

export const DEFAULT_GROUP = 'onboarding';
export const REG_VARIANT_EXPERIMENT = 'regVariant';

export const onboardingVariantsMap = window?.IH?.abSplits?.onboarding || {
  zebra: 100,
};

export const activeOnboardingVariants = Object.keys(onboardingVariantsMap);

export const onboardingLiteVariantsMap = window?.IH?.abSplits
  ?.onboardingLite || { gamora: 100 };
export const activeOnboardingLiteVariants = Object.keys(
  onboardingLiteVariantsMap
);

export const techOnboardingLiteVariantsMap = window?.IH?.abSplits
  ?.techOnboardingLite || { antz: 100 };
export const techActiveOnboardingLiteVariants = Object.keys(
  techOnboardingLiteVariantsMap
);

export const techOnboardingVariantsMap = window?.IH?.abSplits
  ?.techOnboarding || {
  d: 100,
};
export const techOnboardingVariants = Object.keys(techOnboardingVariantsMap);

export const onboardingResumeParseUploadCopyVariantsMap = window?.IH?.abSplits
  ?.onboardingResumeParseUploadCopy || {
  unenrolled: 100,
};

export const variantGroupMap = {
  onboarding: {
    activeVariants: activeOnboardingVariants,
    pathRoot: '/talent_onboard/',
    validVariants: [...activeOnboardingVariants],
    variantCharLookup: {},
    weightedActiveVariantsMap: onboardingVariantsMap,
  },
  onboardingLite: {
    activeVariants: activeOnboardingLiteVariants,
    pathRoot: '/users/onboard/',
    validVariants: [...activeOnboardingLiteVariants],
    variantCharLookup: {},
    weightedActiveVariantsMap: onboardingLiteVariantsMap,
  },
  techOnboardingLite: {
    activeVariants: techActiveOnboardingLiteVariants,
    pathRoot: '/users/tech/onboard/',
    validVariants: [...techActiveOnboardingLiteVariants],
    variantCharLookup: {},
    weightedActiveVariantsMap: techOnboardingLiteVariantsMap,
  },
  onboardingLiteResume: {
    activeVariants: activeOnboardingLiteVariants,
    pathRoot: '/users/sign_up/resume',
    validVariants: activeOnboardingLiteVariants,
    variantCharLookup: {},
    weightedActiveVariantsMap:
      window?.IH?.abSplits?.onboardingLiteResume || onboardingLiteVariantsMap,
  },
  onboardingLiteSalary: {
    activeVariants: activeOnboardingLiteVariants,
    pathRoot: '/users/sign_up/salary',
    validVariants: activeOnboardingLiteVariants,
    variantCharLookup: {},
    weightedActiveVariantsMap:
      window?.IH?.abSplits?.onboardingLiteSalary || onboardingLiteVariantsMap,
  },
  techOnboardingLiteSalary: {
    activeVariants: techActiveOnboardingLiteVariants,
    pathRoot: '/users/tech/sign_up/salary',
    validVariants: techActiveOnboardingLiteVariants,
    variantCharLookup: {},
    weightedActiveVariantsMap: techOnboardingLiteVariantsMap,
  },
  onboardingLiteEducation: {
    activeVariants: activeOnboardingLiteVariants,
    pathRoot: '/users/sign_up/education',
    validVariants: activeOnboardingLiteVariants,
    variantCharLookup: {},
    weightedActiveVariantsMap:
      window?.IH?.abSplits?.onboardingLiteEducation ||
      onboardingLiteVariantsMap,
  },
  onboardingLiteAdvice: {
    activeVariants: activeOnboardingLiteVariants,
    pathRoot: '/users/sign_up/advice',
    validVariants: activeOnboardingLiteVariants,
    variantCharLookup: {},
    weightedActiveVariantsMap:
      window?.IH?.abSplits?.onboardingLiteAdvice || onboardingLiteVariantsMap,
  },
  techOnboarding: {
    activeVariants: techOnboardingVariants,
    pathRoot: '/tech_onboard/',
    validVariants: [...techOnboardingVariants],
    variantCharLookup: {},
    weightedActiveVariantsMap:
      window?.IH?.abSplits?.techOnboarding || techOnboardingVariantsMap,
  },
  onboardingLiteInterviewPractice: {
    activeVariants: activeOnboardingLiteVariants,
    pathRoot: '/users/sign_up/interview_practice',
    validVariants: activeOnboardingLiteVariants,
    variantCharLookup: {},
    weightedActiveVariantsMap:
      window?.IH?.abSplits?.onboardingLiteInterviewPractice ||
      onboardingLiteVariantsMap,
  },
};

export const getRegVariantChar = (variant, variantGroup = DEFAULT_GROUP) =>
  variantGroupMap[variantGroup].variantCharLookup[variant] || variant[0];

export const charToVariantMap = (variantGroup = DEFAULT_GROUP) =>
  variantGroupMap[variantGroup].validVariants.reduce((map, variant) => {
    const variantChar = getRegVariantChar(variant, variantGroup);
    return {
      ...map,
      [variantChar]: variant,
    };
  }, {});

export const pathToVariantChar = (path, variantGroup = DEFAULT_GROUP) => {
  const { pathRoot } = variantGroupMap[variantGroup];
  const pathRE = new RegExp(String.raw`${pathRoot}(\w)`);
  const pathMatch = path?.match(pathRE);
  if (pathMatch && pathMatch[1]) {
    return pathMatch[1];
  }
  return null;
};

export const pathToVariant = (path, variantGroup = DEFAULT_GROUP) =>
  charToVariantMap(variantGroup)[pathToVariantChar(path, variantGroup)];

const getOverrideHash = () => {
  const query = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  const queryHash = query.abTest || {};

  const cookieString = Cookie.get('abTestOverride');
  const cookieHash = cookieString ? JSON.parse(cookieString) : {};

  const overrideHash = { ...cookieHash, ...queryHash };
  Cookie.set('abTestOverride', JSON.stringify(overrideHash));
  return overrideHash;
};

const getVariantFromIndex = (index, weightedVariantsMap) => {
  let weightSum = 0;
  let i = 0;
  const variants = Object.keys(weightedVariantsMap);
  while (i < variants.length) {
    weightSum += weightedVariantsMap[variants[i]];
    if (index < weightSum) {
      return variants[i];
    }
    i += 1;
  }
  return variants[variants.length - 1];
};

export function getUserVariant(
  experimentName,
  weightedVariantsMap,
  { legacyCalculation = false } = {}
) {
  if (weightedVariantsMap == null) return null;
  if (Object.keys(weightedVariantsMap).length === 0) return null;

  const totalWeight = Object.values(weightedVariantsMap).reduce(
    (sum, value) => sum + parseInt(value, 10),
    0
  );

  // Include experiment name in hash to make experiments independent
  const hashInput = legacyCalculation
    ? window.IH.userUuid
    : `${experimentName}:${window.IH.userUuid}`;

  const index = stringhash(hashInput) % totalWeight;
  const selectedVariant = getVariantFromIndex(index, weightedVariantsMap);
  return selectedVariant;
}

const activateABTest = (
  variantGroup,
  { variantOverride, legacyCalculation = false } = {}
) => {
  // TODO: WEB-6294 - log to sentry if window.IH.abSplits is not set
  if (!window?.IH?.abSplits) {
    Sentry.setContext('onboarding info', {
      variantGroup,
    });
    Sentry.captureException(
      new Error('Onboarding: window.IH.abSplits is not set')
    );
  }

  const overrideHash = getOverrideHash();
  const variantGroupObj = variantGroupMap[variantGroup];
  const { validVariants, weightedActiveVariantsMap } = variantGroupObj;
  if (
    overrideHash[REG_VARIANT_EXPERIMENT] &&
    validVariants.includes(overrideHash[REG_VARIANT_EXPERIMENT])
  ) {
    return overrideHash[REG_VARIANT_EXPERIMENT];
  }
  if (variantOverride && validVariants.includes(variantOverride)) {
    return variantOverride;
  }
  return getUserVariant(variantGroup, weightedActiveVariantsMap, {
    legacyCalculation,
  });
};

export default activateABTest;
